import { PendingSecondaryProQaLaunch } from '@mark43/cad-api';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../../action-broadcaster/use-integrated-action-broadcaster';
import { Discipline, generateTempIncidentId } from '../helpers/pro-qa-buttons-helpers';

type DisciplineSelectOptions = { isSecondarySession?: boolean };
type DisciplineSelectHandler = (
    discipline: Discipline,
    launchIncidentId?: string,
    options?: DisciplineSelectOptions
) => void;

export function useProQaSecondaryLaunchActionHandler(args: {
    eventId?: number;
    agencyEventNumber?: string;
    temporaryIncidentIdsRef: MutableRefObject<Record<Discipline, string | undefined>>;
    handleDisciplineSelect: DisciplineSelectHandler;
    consumePendingSecondaryLaunches: (
        agencyEventNumber: string
    ) => Promise<PendingSecondaryProQaLaunch[] | undefined>;
}) {
    const {
        eventId,
        agencyEventNumber,
        temporaryIncidentIdsRef,
        handleDisciplineSelect,
        consumePendingSecondaryLaunches,
    } = args;
    const previousEventIdRef = useRef<number | undefined>(eventId);
    const processedLaunchKeysRef = useRef<Set<string>>(new Set());
    const lastPendingLaunchFetchKeyRef = useRef<string>();

    useEffect(() => {
        if (previousEventIdRef.current !== eventId) {
            processedLaunchKeysRef.current.clear();
            previousEventIdRef.current = eventId;
            lastPendingLaunchFetchKeyRef.current = undefined;
        }
    }, [eventId]);

    const consumePendingSecondaryLaunchesForEvent = useCallback(
        (targetAgencyEventNumber: string | undefined | null) => {
            const resolvedEventNumber = targetAgencyEventNumber?.trim();
            if (!resolvedEventNumber) {
                return;
            }
            if (lastPendingLaunchFetchKeyRef.current === resolvedEventNumber) {
                return;
            }

            lastPendingLaunchFetchKeyRef.current = resolvedEventNumber;
            consumePendingSecondaryLaunches(resolvedEventNumber)
                .then((pendingLaunches) => {
                    pendingLaunches?.forEach(({ targetCadDiscipline, eventNumber }) => {
                        const normalizedDiscipline = normalizeDiscipline(targetCadDiscipline);
                        if (!eventNumber || !normalizedDiscipline) {
                            return;
                        }
                        if (
                            isAlreadyProcessedLaunch(
                                processedLaunchKeysRef.current,
                                normalizedDiscipline,
                                eventNumber
                            )
                        ) {
                            return;
                        }
                        handleDisciplineSelect(normalizedDiscipline, eventNumber, {
                            isSecondarySession: true,
                        });
                    });
                })
                .catch(() => {
                    if (lastPendingLaunchFetchKeyRef.current === resolvedEventNumber) {
                        lastPendingLaunchFetchKeyRef.current = undefined;
                    }
                });
        },
        [consumePendingSecondaryLaunches, handleDisciplineSelect]
    );

    const proQaSecondaryLaunchActionHandler = useCallback<ActionHandler<'EVENT_V2'>>(
        (action) => {
            if (action.type === 'PRO_QA_CONSUME_PENDING_SECONDARY_LAUNCHES') {
                consumePendingSecondaryLaunchesForEvent(action?.payload?.agencyEventNumber);
                return true;
            }

            if (action.type !== 'PRO_QA_SECONDARY_LAUNCH' || !eventId) {
                // primary event is a draft and the secondary launch should only happen once the save completes.
                return false;
            }
            const normalizedTargetDiscipline = normalizeDiscipline(
                action.payload.targetCadDiscipline
            );
            if (!normalizedTargetDiscipline) {
                return false;
            }
            const existingTempId = temporaryIncidentIdsRef.current[normalizedTargetDiscipline];
            const trimmedSavedEventNumber = agencyEventNumber?.trim();
            const incidentIdForLaunch = trimmedSavedEventNumber
                ? trimmedSavedEventNumber
                : existingTempId ?? generateTempIncidentId();

            if (
                isAlreadyProcessedLaunch(
                    processedLaunchKeysRef.current,
                    normalizedTargetDiscipline,
                    incidentIdForLaunch
                )
            ) {
                return false;
            }

            handleDisciplineSelect(normalizedTargetDiscipline, incidentIdForLaunch, {
                isSecondarySession: true,
            });
            return true;
        },
        [
            agencyEventNumber,
            consumePendingSecondaryLaunchesForEvent,
            eventId,
            handleDisciplineSelect,
            temporaryIncidentIdsRef,
        ]
    );

    useIntegratedActionBroadcaster({
        windowType: 'EVENT_V2',
        handler: proQaSecondaryLaunchActionHandler,
        ignoreActiveWorkspaceWindowType: true,
    });
}

function normalizeDiscipline(value?: string | null): Discipline | undefined {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim().toUpperCase();
    switch (trimmed) {
        case 'P':
            return 'POLICE';
        case 'F':
            return 'FIRE';
        case 'M':
            return 'EMS';
        default:
            return undefined;
    }
}

/**
 * De-duplicates secondary launches by tracking `discipline:identifier` keys.
 * Returns true if the launch was already processed (duplicate), false if it is new and should proceed.
 */
function isAlreadyProcessedLaunch(
    processedKeys: Set<string>,
    discipline: Discipline,
    identifier: string | number | null | undefined
): boolean {
    if (identifier === undefined || identifier === null) {
        return false;
    }
    const key = `${discipline}:${String(identifier)}`;
    if (processedKeys.has(key)) {
        return true;
    }
    processedKeys.add(key);
    return false;
}
